/**
 * The catalog's palette comparison and colour-vision simulation (6-oct-2026): the design system on the
 * approved Bronze palette (src/ui/colors.css) or on today's, until the app reloads; and the catalog seen as part of
 * a kitchen sees it.
 *
 * @rule   The comparison only ever writes `data-bz-palette` on <html>: `current` brings tokens.css's values
 *         back, and going back to Bronze removes it, so colors.css is what draws again.
 * @rule   The simulation only filters the catalog, never the app: a filter over the camera's live preview
 *         would be paid on every frame of a €100 tablet (norm 1).
 */

/** The ramps of colors.css, in its order, each with the job it does in the system. */
export const RAMPS = ['grey', 'blue', 'bronze', 'green', 'orange', 'amber', 'red', 'slate'];

/** The two colours Adrián gave, exact on step 9 of their ramps. */
export const GIVEN = ['--bz-blue-9', '--bz-bronze-9'];

/** The palettes the comparison offers: Bronze (colors.css's own) and today's. */
export const PALETTES = ['bronze', 'current'];

/** Which palette the comparison has set, read from the document (it lives until a reload). */
export function currentPalette() {
    const set = document.documentElement.dataset.bzPalette;
    return PALETTES.includes(set) ? set : 'bronze';
}

/** Paints the system with a palette; Bronze leaves colors.css's own. */
export function applyPalette(choice) {
    const root = document.documentElement;
    if (choice !== 'bronze' && PALETTES.includes(choice)) root.dataset.bzPalette = choice;
    else delete root.dataset.bzPalette;
}

/**
 * Every step each ramp has, with its value, as the document declares it: the ramps are generated
 * (scripts/ui-colors.mjs), so the catalog reads them instead of keeping a copy that could drift.
 */
export function readRamps(style = getComputedStyle(document.documentElement)) {
    return RAMPS.map((name) => ({
        name,
        steps: Array.from({ length: 12 }, (_, i) => i + 1)
            .map((step) => ({ step, token: `--bz-${name}-${step}`, value: style.getPropertyValue(`--bz-${name}-${step}`).trim() }))
            .filter((s) => s.value),
    }));
}

/**
 * How a colour-vision deficiency sees: Machado, Oliveira and Fernandes's matrices (2009), severity 1, on
 * linear sRGB, which is where an SVG filter works by default. scripts/ui-colors.mjs measures the palette
 * with the same ones (tests/unit/ui.colorTest.spec.js holds them equal). Achromatopsia keeps luminance only.
 */
export const VISION_MATRICES = {
    deuteranopia: [[0.367322, 0.860646, -0.227968], [0.280085, 0.672501, 0.047413], [-0.01182, 0.04294, 0.968881]],
    protanopia: [[0.152286, 1.052583, -0.204868], [0.114503, 0.786281, 0.099216], [-0.003882, -0.048116, 1.051998]],
    tritanopia: [[1.255528, -0.076749, -0.178779], [-0.078411, 0.930809, 0.147602], [0.004733, 0.691367, 0.3039]],
    achromatopsia: [[0.2126, 0.7152, 0.0722], [0.2126, 0.7152, 0.0722], [0.2126, 0.7152, 0.0722]],
};

/**
 * A kitchen's glare: the light a glossy screen reflects, added to every channel as a share of its white.
 * 0.10 is the stress test the palette is measured with (scripts/ui-colors.mjs, `underGlare`).
 */
export const GLARE = 0.1;

/** The choices of «See as», normal vision first. */
export const VISIONS = ['normal', ...Object.keys(VISION_MATRICES), 'glare'];

/** A 3 × 3 matrix as feColorMatrix's twenty values (alpha untouched). */
export const feMatrix = (m) => [...m.flatMap((row) => [...row, 0, 0]), 0, 0, 0, 1, 0].join(' ');
